import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Col,
  Container,
  Dropdown,
  Form,
  Row,
} from 'react-bootstrap'
import toast from 'react-hot-toast'
import CloudinaryUploadInput from 'components/cloudinary-upload-input'
import { useSelector } from 'react-redux'
import { store, RootState } from 'index'
import { BASE_API_URL } from 'constants/env'
import {
  BUG_REPORT_OUTCOME_UNKNOWN,
  BugReportRequestError,
  BugReportResult,
  bugReportSession,
  sameBugReportSession,
  submitBugReport,
} from 'utils/bug-report-request'
import {
  markBugReportSubmission,
  readBugReportSubmission,
} from 'utils/bug-report-submission'

const BugReporterForm = ({
  session,
  reporterEmail,
}: {
  session: ReturnType<typeof bugReportSession> & { userId: string | number }
  reporterEmail: string
}) => {
  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [deviceDetails, setDeviceDetails] = useState<string>('')
  const [otherDeviceDetails, setOtherDeviceDetails] = useState<string>('')
  const [issueType, setIssueType] = useState<string>('')
  const [otherIssueType, setOtherIssueType] = useState<string>('')
  const [hubbleURL, setHubbleURL] = useState<string>('')
  const [loomVideoLink, setLoomVideoLink] = useState<string>('')
  const [email, setEmail] = useState<string>(reporterEmail)
  const [priority, setPriority] = useState<number | null>(null)
  const [imageUrl, setImageUrl] = useState<string | undefined>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [created, setCreated] = useState<BugReportResult | null>(null)
  const [previousSubmission] = useState(() =>
    readBugReportSubmission(session.userId)
  )
  const [outcomeUnknown, setOutcomeUnknown] = useState(
    previousSubmission.pending
  )
  const [durable, setDurable] = useState(previousSubmission.durable)
  const pending = useRef(previousSubmission.pending)
  const mounted = useRef(true)
  const formGeneration = useRef(0)
  const uploadGeneration = formGeneration.current
  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  const clearForm = () => {
    formGeneration.current += 1
    setPriority(null)
    setTitle('')
    setDescription('')
    setHubbleURL('')
    setDeviceDetails('')
    setOtherDeviceDetails('')
    setIssueType('')
    setOtherIssueType('')
    setLoomVideoLink('')
    setImageUrl('')
  }

  const isCurrent = () =>
    mounted.current &&
    sameBugReportSession(session, bugReportSession(store.getState()))

  const createIssue = async (e: React.FormEvent) => {
    e.preventDefault()
    // The ref closes the gap before React renders its disabled button.
    if (pending.current || !isCurrent()) return
    if (!title.trim() || !description.trim() || !email.trim()) {
      toast.error('Title, description, and reporter email are required.')
      return
    }
    pending.current = true
    setIsLoading(true)
    setCreated(null)
    setDurable(markBugReportSubmission(session.userId, true))
    try {
      const result = await submitBugReport({
        title,
        description,
        priority,
        reporter_email: email,
        device_details: deviceDetails,
        other_device_details: otherDeviceDetails,
        issue_type: issueType,
        other_issue_type: otherIssueType,
        hubble_url: hubbleURL,
        loom_video_url: loomVideoLink,
        file_url: imageUrl || '',
      })
      if (!isCurrent()) return
      setDurable(markBugReportSubmission(session.userId, false))
      pending.current = false
      setCreated(result)
      clearForm()
    } catch (error) {
      if (!isCurrent()) return
      if (!(error instanceof BugReportRequestError) || error.outcomeUnknown) {
        // Keep the marker and block another creation even after a refresh.
        setOutcomeUnknown(true)
      } else {
        setDurable(markBugReportSubmission(session.userId, false))
        pending.current = false
        toast.error(error.message)
      }
    } finally {
      if (isCurrent()) setIsLoading(false)
    }
  }

  const startNewReport = () => {
    if (!isCurrent()) return
    setDurable(markBugReportSubmission(session.userId, false))
    pending.current = false
    setOutcomeUnknown(false)
    clearForm()
  }

  return (
    <Container>
      <h1 className="text-center">Submit Linq Engineering Issue</h1>

      <div className="mb-3 text-center">
        Details of this form will be submitted to Bug Support in Linear. Please
        fill out all details as accurately as possible.
      </div>

      {created && (
        <Alert
          variant={created.warnings.length ? 'warning' : 'success'}
          role="status"
        >
          Created{' '}
          <a href={created.issue.url} target="_blank" rel="noreferrer">
            {created.issue.identifier}
          </a>
          .
          {created.warnings.length > 0 && (
            <>
              <p>
                The issue exists. Add any missing details to that issue; do not
                submit it again.
              </p>
              <ul>
                {created.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </>
          )}
        </Alert>
      )}
      {outcomeUnknown && (
        <Alert variant="warning" role="alert">
          <p>{BUG_REPORT_OUTCOME_UNKNOWN}</p>
          <Button variant="outline-dark" onClick={startNewReport}>
            I checked Linear; start a new report
          </Button>
        </Alert>
      )}
      {!durable && (
        <Alert variant="warning" role="alert">
          This browser cannot save submission status. Keep this page open until
          the result appears. If you reload or leave, check Linear before
          submitting the report again.
        </Alert>
      )}
      <Form onSubmit={createIssue} className="border border-1 p-4 rounded-3">
        <fieldset disabled={isLoading || outcomeUnknown}>
          <Row>
            <Col lg={6}>
              <Form.Group className="mb-3" controlId="title">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Bug Title"
                  value={title}
                  maxLength={255}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <div>
                  <Form.Text className="text-muted">
                    Include as many relevant details as possible, a link to a
                    Slack thread for context, or a loom video link.
                  </Form.Text>
                </div>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter Description"
                  value={description}
                  maxLength={20000}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="issueType">
                <Form.Label>Issue Type</Form.Label>
                <div>
                  <Form.Text className="text-muted">
                    Select which system or integration is experiencing the
                    issue.
                  </Form.Text>
                </div>
                <Form.Control
                  as="select"
                  value={issueType}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setIssueType(e.target.value)
                  }
                >
                  <option value="">Select Issue Type</option>
                  <option value="linq app">Linq App</option>
                  <option value="ghl integration">GHL Integration</option>
                  <option value="salesforce integration">
                    Salesforce Integration
                  </option>
                  <option value="hubspot integration">
                    HubSpot Integration
                  </option>
                  <option value="linq partner api">Linq Partner API</option>
                  <option value="zapier">Zapier</option>
                  <option value="other">Other</option>
                </Form.Control>
                {issueType === 'other' && (
                  <div className="mt-2">
                    <Form.Control
                      type="text"
                      placeholder="Please specify issue type"
                      value={otherIssueType}
                      maxLength={500}
                      onChange={(e) => setOtherIssueType(e.target.value)}
                    />
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="loomVideoLink">
                <Form.Label>Loom Video Link</Form.Label>
                <div>
                  <Form.Text className="text-muted">
                    Please add a loom.com video - the best way to make sure bugs
                    get fixed is to provide as much info as possible.
                  </Form.Text>
                </div>
                <Form.Control
                  type="text"
                  placeholder="Enter Loom Video URL"
                  value={loomVideoLink}
                  maxLength={2048}
                  onChange={(e) => setLoomVideoLink(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="hubbleURL">
                <Form.Label>Hubble URL</Form.Label>
                <div>
                  <Form.Text className="text-muted">
                    Hubble URL of the user or organization experiencing the
                    issue.
                  </Form.Text>
                </div>
                <Form.Control
                  type="text"
                  placeholder="Hubble URL"
                  value={hubbleURL}
                  maxLength={2048}
                  onChange={(e) => setHubbleURL(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="deviceDetails">
                <Form.Label>Device Details</Form.Label>
                <div>
                  <Form.Text className="text-muted">
                    Select the device type where the issue is occurring.
                  </Form.Text>
                </div>
                <Form.Control
                  as="select"
                  value={deviceDetails}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setDeviceDetails(e.target.value)
                  }
                >
                  <option value="">Select Device Type</option>
                  <option value="mobile ios">Mobile iOS</option>
                  <option value="mobile android">Mobile Android</option>
                  <option value="desktop macos">Desktop macOS</option>
                  <option value="desktop windows">Desktop Windows</option>
                  <option value="other">Other</option>
                </Form.Control>
                {deviceDetails === 'other' && (
                  <div className="mt-2">
                    <Form.Control
                      type="text"
                      placeholder="Please specify device details"
                      value={otherDeviceDetails}
                      maxLength={500}
                      onChange={(e) => setOtherDeviceDetails(e.target.value)}
                    />
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Dropdown className="mb-3">
                <Form.Label>
                  Priority ({' '}
                  <Form.Text className="text-muted">
                    <a
                      href="https://www.notion.so/Bug-Reporting-and-Triaging-ad28cb4d7b464eb29eca7e7b3815a480?pvs=4"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Notion Doc explaining priorities
                    </a>
                  </Form.Text>{' '}
                  )
                </Form.Label>
                <div>
                  <Dropdown.Toggle variant="success" className="mt-1">
                    {priority === 1
                      ? 'S0 - Urgent'
                      : priority === 2
                      ? 'S1 - High'
                      : priority === 3
                      ? 'S2 - Medium'
                      : priority === 4
                      ? 'S4 - Low'
                      : priority === 0
                      ? 'No priority'
                      : 'Select Priority'}
                  </Dropdown.Toggle>
                </div>
                <Dropdown.Menu>
                  <Dropdown.Item
                    disabled={isLoading || outcomeUnknown}
                    onClick={() => setPriority(1)}
                  >
                    S0 - Urgent
                  </Dropdown.Item>
                  <Dropdown.Item
                    disabled={isLoading || outcomeUnknown}
                    onClick={() => setPriority(2)}
                  >
                    S1 - High
                  </Dropdown.Item>
                  <Dropdown.Item
                    disabled={isLoading || outcomeUnknown}
                    onClick={() => setPriority(3)}
                  >
                    S2 - Medium
                  </Dropdown.Item>
                  <Dropdown.Item
                    disabled={isLoading || outcomeUnknown}
                    onClick={() => setPriority(4)}
                  >
                    S4 - Low
                  </Dropdown.Item>
                  <Dropdown.Item
                    disabled={isLoading || outcomeUnknown}
                    onClick={() => setPriority(0)}
                  >
                    No priority
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <Form.Group className="mb-3" controlId="reporterEmail">
                <Form.Label>Email Address</Form.Label>
                <div>
                  <Form.Text className="text-muted">
                    This is your linqapp.com email address, don't put a
                    customer's email here.
                  </Form.Text>
                </div>
                <Form.Control
                  type="text"
                  placeholder="Enter Email"
                  maxLength={254}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="imageUrl">
                <Form.Label>Screenshot/Video File Upload</Form.Label>
                <div>
                  <Form.Text className="text-muted">
                    (Include a relevant screenshot or screen recording here if
                    possible)
                  </Form.Text>
                </div>
                <CloudinaryUploadInput
                  inputLabel=""
                  imageUrl={imageUrl}
                  onImageUrlChange={(value) => {
                    if (
                      isCurrent() &&
                      !pending.current &&
                      uploadGeneration === formGeneration.current
                    )
                      setImageUrl(value)
                  }}
                />
              </Form.Group>
            </Col>
            <Button
              variant="primary"
              type="submit"
              size="lg"
              className="w-100"
              disabled={isLoading || outcomeUnknown}
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </Row>
        </fieldset>
      </Form>
    </Container>
  )
}

const BugReporterPage = () => {
  const session = useSelector(bugReportSession, sameBugReportSession)
  const email = useSelector(
    (state: RootState) => state.user.currentUser?.email || ''
  )
  if (!BASE_API_URL || !session.token || !session.userId || !session.admin) {
    return (
      <Container>
        <Alert variant="warning">
          Sign in with an admin account to report a bug.
        </Alert>
      </Container>
    )
  }
  // Remount all form fields on identity/token changes so drafts and late results
  // from the previous sign-in never carry into a different session.
  return (
    <BugReporterForm
      key={`${session.userId}:${session.token}`}
      session={{ ...session, userId: session.userId }}
      reporterEmail={email}
    />
  )
}

export default BugReporterPage
